import React, { useState, useEffect, useRef, useCallback } from "react";
import CustomerLayout from "../../../layouts/CustomerLayout";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { States } from "../../../data/State";
import { setUser, clearUser, updateSubscriptionData } from "../../../redux/userSlice";
import profileRequests from "../../../api/Customer/ProfileRequests";
import notificationRequests from "../../../api/Customer/NotificationRequests";
import { toast } from "react-toastify";
import { motion } from "motion/react";
import {
  Crown,
  CreditCard,
  TrendingUp,
  Users,
  CheckCircle,
  Loader2,
  User,
  Mail,
  Phone,
  MapPin,
  Building,
  Calendar,
  Shield,
  Edit,
  Save,
  X,
  Check,
  Star,
  CheckCircle2,
  Sparkles,
  Bell,
  MessageSquare,
  AlertTriangle,
  Clock,
  DollarSign,
  RefreshCw,
  Info,
} from "lucide-react";
import landingPageRequests from "../../../api/Guest/LandingPageRequests";
import subscriptionRequests from "../../../api/Customer/SubscriptionRequests";
import PaymentConfirmation from "../../../components/subscription/PaymentConfirmation";
import PackageGrid from "../../../components/subscription/PackageGrid";
import { modifyPackagePrices } from "../../../utils/packageUtils";

const SubscriptionTabContent = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user?.txtconnect_ng_user);
  const subscriptionPlan = useSelector(
    (state) => state.user?.txtconnect_ng_user?.subscription_plan
  );
  const [packages, setPackages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedDuration, setSelectedDuration] = useState("1 month");
  const [selectedPackageName, setSelectedPackageName] = useState(null);

  useEffect(() => {
    fetchSubscriptionPackages();
  }, []);

  // Refresh subscription data when Subscription tab is opened (same shape as active_subscription API)
  useEffect(() => {
    const refreshSubscription = async () => {
      try {
        const response = await subscriptionRequests.getCurrentUserSubscriptionData();
        if (response.status_code === "000" && response.data) {
          const { subscription_plan, plan_name, units } = response.data;
          dispatch(
            updateSubscriptionData({
              subscriptionData: subscription_plan,
              plan_name,
              units,
            })
          );
        }
      } catch (err) {
        console.error("Error refreshing subscription:", err);
      }
    };
    refreshSubscription();
  }, [dispatch]);

  const fetchSubscriptionPackages = async () => {
    try {
      setIsLoading(true);
      const response = await landingPageRequests.getSubscriptionPlans();
      if (response.data.status_code === "000") {
        const sortedPackages = response.data.data.sort((a, b) => {
          const order = { Bronze: 0, Silver: 1, Gold: 2, Platinum: 3 };
          return order[a.name] - order[b.name];
        });
        const modifiedPackages = modifyPackagePrices(sortedPackages);
        setPackages(modifiedPackages);
      } else {
        toast.error(response.data.reason);
      }
    } catch (error) {
      toast.error("Failed to load subscription packages");
      console.error("Error fetching packages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "GHS",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  const handleUpgrade = (packageData, packageName) => {
    setSelectedPackageName(packageName);
    setSelectedPlan(packageData);
    setShowPaymentConfirmation(true);
  };

  const handleDowngrade = (packageData, packageName) => {
    setSelectedPackageName(packageName);
    setSelectedPlan(packageData);
    setShowPaymentConfirmation(true);
  };

  const handleBackToPlans = () => {
    setSelectedPackageName(null);
    setShowPaymentConfirmation(false);
    setSelectedPlan(null);
  };

  const handleProceedToPayment = () => {
    // Payment is handled by PaymentConfirmation component
    // This is kept for compatibility but not used
  };

  const durations = ["1 month", "2 months", "3 months", "No Expiry"];

  // Show PaymentConfirmation component instead of modal
  if (showPaymentConfirmation && selectedPlan) {
    return (
      <PaymentConfirmation
        selectedPlan={selectedPlan}
        selectedPackageName={selectedPackageName}
        selectedDuration={selectedDuration}
        onBack={handleBackToPlans}
        onProceedToPayment={handleProceedToPayment}
      />
    );
  }

  // Check if current plan is Postpaid
  const isPostpaid = subscriptionPlan?.name === "Postpaid";

  return (
    <div className="mx-auto space-y-6 max-w-8xl">
      {/* Postpaid Plan Display */}
      {isPostpaid ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-[1.01] transition-transform duration-300"
        >
          <div className="overflow-hidden relative bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2 bg-white/10"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2 bg-white/10"></div>
            <div className="relative px-6 py-5">
              <div className="flex justify-between items-center">
                <h2 className="flex items-center text-xl font-semibold text-white">
                  <Crown className="mr-2 w-6 h-6" />
                  Custom Plan - Custom Enterprise Solution
                </h2>
                <div className="flex gap-2 items-center px-4 py-2 rounded-full border backdrop-blur-sm bg-white/20 border-white/30">
                  <Star className="w-4 h-4 text-yellow-300" />
                  <span className="text-sm font-semibold text-white">Premium</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <div className="overflow-hidden relative p-8 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 rounded-xl border-2 border-amber-300/50">
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full blur-2xl translate-x-1/2 -translate-y-1/2 bg-amber-200/20"></div>
              
              <div className="relative">
                <div className="flex flex-col gap-6 mb-6 lg:flex-row lg:items-center lg:justify-between">
                  {/* Plan Info */}
                  <div className="flex-1">
                    <div className="flex gap-3 items-center mb-4">
                      <div className="flex justify-center items-center w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-lg">
                        <Crown className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <div className="flex gap-2 items-center mb-1">
                          <h3 className="text-2xl font-bold text-slate-900">Custom Plan</h3>
                          <span className="px-3 py-1 text-xs font-semibold text-amber-800 bg-amber-200 rounded-full border border-amber-300">
                            Custom Enterprise
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">Your personalized business solution with flexible billing</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Premium Badge */}
                  <div className="flex justify-center lg:justify-end">
                    <div className="p-5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl border-2 shadow-lg border-amber-400/50">
                      <div className="text-center">
                        <Sparkles className="mx-auto mb-2 w-10 h-10 text-white" />
                        <p className="text-sm font-semibold text-white">Enterprise</p>
                        <p className="text-xs text-amber-100">Custom Plan</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6 md:grid-cols-4">
                  <div className="p-4 rounded-xl border backdrop-blur-sm bg-white/80 border-amber-200/50">
                    <p className="mb-1 text-xs font-medium text-slate-600">Units Available</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {formatNumber(parseInt(user?.units || subscriptionPlan?.units || 0))}
                    </p>
                  </div>
                  <div className="p-4 rounded-xl border backdrop-blur-sm bg-white/80 border-amber-200/50">
                    <p className="mb-1 text-xs font-medium text-slate-600">Billing Type</p>
                    <p className="text-2xl font-bold text-slate-900">Customized Plan</p>
                  </div>
                  <div className="p-4 rounded-xl border backdrop-blur-sm bg-white/80 border-amber-200/50">
                    <p className="mb-1 text-xs font-medium text-slate-600">Status</p>
                    <div className="flex gap-1 items-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <p className="text-2xl font-bold text-green-600">Active</p>
                    </div>
                  </div>
                  <div className="p-4 rounded-xl border backdrop-blur-sm bg-white/80 border-amber-200/50">
                    <p className="mb-1 text-xs font-medium text-slate-600">Plan Type</p>
                    <p className="text-2xl font-bold text-slate-900">Enterprise Plan</p>
                  </div>
                </div>
                
                {/* Benefits */}
                <div className="flex flex-wrap gap-3 pt-6 border-t border-amber-200/50">
                  <div className="flex gap-2 items-center px-4 py-2 rounded-lg border bg-white/60 border-amber-200/50">
                    <CheckCircle2 className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-slate-700">Flexible Billing</span>
                  </div>
                  <div className="flex gap-2 items-center px-4 py-2 rounded-lg border bg-white/60 border-amber-200/50">
                    <CheckCircle2 className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-slate-700">Dedicated Support</span>
                  </div>
                  <div className="flex gap-2 items-center px-4 py-2 rounded-lg border bg-white/60 border-amber-200/50">
                    <CheckCircle2 className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-slate-700">Custom Features</span>
                  </div>
                  <div className="flex gap-2 items-center px-4 py-2 rounded-lg border bg-white/60 border-amber-200/50">
                    <CheckCircle2 className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-slate-700">Priority Access</span>
                  </div>
                  <div className="flex gap-2 items-center px-4 py-2 rounded-lg border bg-white/60 border-amber-200/50">
                    <CheckCircle2 className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-slate-700">Scalable Solutions</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      ) : (
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-[1.02] transition-transform duration-300">
          <div className="px-6 py-4 bg-gradient-to-r from-purple-600 to-pink-600">
            <h2 className="flex items-center text-xl font-semibold text-white">
              <span className="mr-2">💳</span>
              Current Subscription
            </h2>
          </div>
          <div className="p-6">
            {/* Current Plan Details */}
            <div className="mb-8">
              <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200 transition-colors duration-200 hover:border-purple-300">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="flex items-center text-xl font-semibold text-gray-900">
                    <span className="mr-2">📋</span>
                    Current Plan
                  </h3>
                  <span className="inline-flex items-center px-4 py-2 text-sm font-medium text-purple-800 bg-purple-100 rounded-full shadow-md">
                    <span className="mr-1">⭐</span>
                    {subscriptionPlan?.name || "Free Tier"}
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                  <div className="p-4 text-center bg-white rounded-lg shadow-sm">
                    <p className="mb-2 text-3xl font-bold text-purple-600">
                      {"Active"}
                    </p>
                    <p className="flex justify-center items-center text-sm text-gray-600">
                      <span className="mr-1">📱</span>
                      Status
                    </p>
                  </div>
                  <div className="p-4 text-center bg-white rounded-lg shadow-sm">
                    <p className="mb-2 text-3xl font-bold text-purple-600">
                      {subscriptionPlan
                        ? formatCurrency(subscriptionPlan.price)
                        : "GH₵0"}
                    </p>
                    <p className="flex justify-center items-center text-sm text-gray-600">
                      <span className="mr-1">💰</span>
                      Cost
                    </p>
                  </div>
                  <div className="p-4 text-center bg-white rounded-lg shadow-sm">
                    <p className="mb-2 text-3xl font-bold text-purple-600">
                      {subscriptionPlan
                        ? formatNumber(parseInt(user?.units))
                        : "0"}
                    </p>
                    <p className="flex justify-center items-center text-sm text-gray-600">
                      <span className="mr-1">⚡</span>
                      Units Left
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duration Selector */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-center items-center mb-6 w-full"
      >
        <div className="flex overflow-x-auto justify-center pb-2 -mb-2 w-full">
          <div className="inline-flex gap-2 p-2 bg-white rounded-full shadow-lg">
            {durations.map((duration) => {
              const getDisplayText = (dur) => {
                switch(dur) {
                  case "1 month": return { short: "1M", full: "1 Month" };
                  case "2 months": return { short: "2M", full: "2 Months" };
                  case "3 months": return { short: "3M", full: "3 Months" };
                  case "No Expiry": return { short: "∞", full: "No Expiry" };
                  default: return { short: dur, full: dur };
                }
              };
              const text = getDisplayText(duration);
              
              return (
                <button
                  key={duration}
                  onClick={() => setSelectedDuration(duration)}
                  className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                    selectedDuration === duration
                      ? "bg-red-600 text-white shadow-md"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <span className="sm:hidden">{text.short}</span>
                  <span className="hidden sm:inline">{text.full}</span>
                </button>
              );
            })}
          </div>
        </div>
      </motion.div>

      {/* Available Plans */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-[1.02] transition-transform duration-300">
        <div className="px-6 py-4 bg-gradient-to-r from-red-600 to-blue-600">
          <h2 className="flex items-center text-xl font-semibold text-white">
            <span className="mr-2">🚀</span>
            Available Plans
          </h2>
        </div>
        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="w-8 h-8 rounded-full border-4 border-blue-600 animate-spin border-t-transparent"></div>
            </div>
          ) : (
            <PackageGrid
              packages={packages}
              onUpgrade={handleUpgrade}
              onDowngrade={handleDowngrade}
              units={user?.units || subscriptionPlan?.units || 0}
              selectedDuration={selectedDuration}
              userDuration={user?.plan_name || user?.subscription_duration || subscriptionPlan?.duration}
            />
          )}
        </div>
      </div>

      {/* Additional Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 gap-6 md:grid-cols-3"
      >
        <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-lg">
          <div className="flex gap-3 items-center mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CreditCard className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">
              Flexible Billing
            </h3>
          </div>
          <p className="text-sm text-gray-600">
            Choose the plan that best fits your messaging needs. All plans
            include unlimited contact groups.
          </p>
        </div>

        <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-lg">
          <div className="flex gap-3 items-center mb-4">
            <div className="p-2 bg-red-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">
              Easy Scaling
            </h3>
          </div>
          <p className="text-sm text-gray-600">
            Upgrade your plan anytime as your business grows. Changes take
            effect immediately.
          </p>
        </div>

        <div className="p-6 bg-white rounded-2xl border border-gray-100 shadow-lg">
          <div className="flex gap-3 items-center mb-4">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Users className="w-5 h-5 text-orange-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800">
              24/7 Support
            </h3>
          </div>
          <p className="text-sm text-gray-600">
            Get help whenever you need it. Our support team is available round
            the clock.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// Notification Settings Component
const NotificationSettingsContent = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState([]);
  const [originalSettings, setOriginalSettings] = useState([]);
  const [activeInfoTooltip, setActiveInfoTooltip] = useState(null);
  const bottomSaveBarRef = useRef(null);
  
  // Available options
  const channelOptions = [
    { value: "email", label: "Email", icon: Mail, description: "Receive notifications via email" },
    { value: "sms", label: "SMS", icon: MessageSquare, description: "Receive notifications via SMS" },
  ];

  // Type options – only Low Balance for now (Package Expiry hidden for later)
  // const typeOptions = [
  //   { value: "low_balance", label: "Low Balance", icon: DollarSign, description: "Get notified when your SMS units reach the threshold" },
  //   { value: "package_expiry", label: "Package Expiry", icon: Calendar, description: "Get notified when your subscription is about to expire" },
  // ];

  const frequencyOptions = [
    { value: "8am", label: "8:00 AM", description: "Receive notifications at 8:00 AM" },
    { value: "1pm", label: "1:00 PM", description: "Receive notifications at 1:00 PM" },
    { value: "6pm", label: "6:00 PM", description: "Receive notifications at 6:00 PM" },
  ];

  // Load notification settings (silent = true skips loading spinner, e.g. after save)
  const loadNotificationSettings = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await notificationRequests.getNotificationSettings();
      console.log("Notification settings response:", response); 
      
      if (response.success) {
        const settingsData = response.data || [];
        
        setSettings(settingsData);
        setOriginalSettings(JSON.parse(JSON.stringify(settingsData)));
      } else if (!silent) {
        toast.error(response.message || "Failed to load notification settings");
      }
    } catch (error) {
      console.error("Error loading notification settings:", error);
      if (!silent) toast.error("Error loading notification settings");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotificationSettings();
  }, [loadNotificationSettings]);

  // Scroll bottom save bar into view when unsaved changes appear (desktop only; mobile has fixed bar)
  useEffect(() => {
    if (loading || settings.length === 0) return;
    const hasUnsaved = JSON.stringify(settings) !== JSON.stringify(originalSettings);
    if (!hasUnsaved) return;
    if (!window.matchMedia("(min-width: 768px)").matches) return;
    requestAnimationFrame(() => {
      bottomSaveBarRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [settings, originalSettings, loading]);

  // Handle channel toggle
  const handleChannelToggle = (notificationSlug, channel) => {

    
    setSettings(prevSettings => 
      prevSettings.map(setting => {
        if (setting.notification_slug === notificationSlug) {
          const currentChannels = setting.channel || [];
          const newChannels = currentChannels.includes(channel)
            ? currentChannels.filter(c => c !== channel)
            : [...currentChannels, channel];
          
          return {
            ...setting,
            channel: newChannels,
          };
        }
        return setting;
      })
    );
  };

  // Handle type toggle (unused while only Low Balance is supported; restore when Package Expiry is re-enabled)
  // const handleTypeToggle = (notificationSlug, type) => {
  //   setSettings(prevSettings => 
  //     prevSettings.map(setting => {
  //       if (setting.notification_slug === notificationSlug) {
  //         const currentTypes = setting.type || [];
  //         const newTypes = currentTypes.includes(type)
  //           ? currentTypes.filter(t => t !== type)
  //           : [...currentTypes, type];
  //         return { ...setting, type: newTypes };
  //       }
  //       return setting;
  //     })
  //   );
  // };

  // Handle frequency toggle (multi-select: 8am, 1pm, 6pm)
  const handleFrequencyToggle = (notificationSlug, timeValue) => {
    setSettings(prevSettings =>
      prevSettings.map(setting => {
        if (setting.notification_slug !== notificationSlug) return setting;
        const current = setting.frequency || [];
        const next = current.includes(timeValue)
          ? current.filter((t) => t !== timeValue)
          : [...current, timeValue];
        return { ...setting, frequency: next };
      })
    );
  };

  // Handle threshold change
  const handleThresholdChange = (notificationSlug, threshold) => {
    const value = parseInt(threshold) || 0;
    setSettings(prevSettings => 
      prevSettings.map(setting => 
        setting.notification_slug === notificationSlug
          ? { ...setting, threshold: Math.max(0, value) }
          : setting
      )
    );
  };

  // Check if settings have changed
  const hasChanges = () => {
    return JSON.stringify(settings) !== JSON.stringify(originalSettings);
  };

  // At least one notification time must be selected per notification to allow save
  const hasValidTimeSelection = () => {
    return settings.every((s) => (s.frequency || []).length >= 1);
  };

  const canSave = () => hasChanges() && hasValidTimeSelection();

  // Save settings
  const handleSaveSettings = async () => {
    if (!hasChanges()) {
      toast.info("No changes to save");
      return;
    }
    if (!hasValidTimeSelection()) {
      toast.error("Please select at least one notification time (8:00 AM, 1:00 PM, or 6:00 PM) for each notification.");
      return;
    }

    try {
      setSaving(true);
      
      // Save each notification setting
      for (const setting of settings) {
        const original = originalSettings.find(s => s.notification_slug === setting.notification_slug);
        
        if (JSON.stringify(setting) !== JSON.stringify(original)) {
          const response = await notificationRequests.updateNotificationSettings(setting);
          
          if (!response.success) {
            toast.error(`Failed to update: ${response.message}`);
            return;
          }
        }
      }
      
      toast.success("Notification settings updated successfully");
      // Refetch from server so UI immediately reflects saved state (no loading spinner)
      await loadNotificationSettings(true);
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Error saving notification settings");
    } finally {
      setSaving(false);
    }
  };

  // Reset to original settings
  const handleReset = () => {
    setSettings(JSON.parse(JSON.stringify(originalSettings)));
    toast.info("Changes discarded");
  };

  // Info tooltip content
  const infoTooltips = {
    channel: "Choose how you want to receive notifications. You can select multiple channels.",
    frequency: "Select one or more times (8:00 AM, 1:00 PM, 6:00 PM) to receive low balance notifications.",
    threshold: "Set the minimum SMS units level that will trigger a low balance alert.",
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-[1.02] transition-transform duration-300">
        <div className="px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-r from-blue-600 to-indigo-600">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="flex items-center text-lg font-semibold text-white sm:text-xl min-w-0">
              <Bell className="mr-2 w-5 h-5 flex-shrink-0" />
              <span className="truncate">Notification Settings</span>
            </h2>
            
            <div className="flex flex-shrink-0 gap-2 flex-wrap">
              <button
                onClick={handleReset}
                disabled={!hasChanges() || saving}
                className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-white bg-white/20 rounded-lg backdrop-blur-sm transition-colors hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="inline w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Reset</span>
              </button>
              
              <button
                onClick={handleSaveSettings}
                disabled={!canSave() || saving}
                title={hasChanges() && !hasValidTimeSelection() ? "Select at least one notification time to save" : undefined}
                className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium text-white bg-green-500 rounded-lg transition-colors hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="inline w-4 h-4 sm:mr-1 border-2 border-white rounded-full animate-spin border-t-transparent"></div>
                    <span className="hidden sm:inline">Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="inline w-4 h-4 sm:mr-1" />
                    <span className="hidden sm:inline">Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </div>
          <p className="mt-2 text-xs sm:text-sm text-blue-100">
            Customize how and when you receive notifications about your account
          </p>
        </div>

        {/* Info Banner */}
        <div className="p-3 sm:p-4 bg-blue-50 border-b border-blue-100">
          <div className="flex items-start gap-2 min-w-0">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs sm:text-sm text-blue-700 min-w-0">
              Configure your notification preferences below. Changes will be applied immediately after saving.
            </p>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex justify-center items-center py-12 bg-white rounded-2xl shadow-xl">
          <div className="flex items-center space-x-3">
            <div className="w-6 h-6 rounded-full border-2 border-blue-500 animate-spin border-t-transparent"></div>
            <span className="text-gray-600">Loading notification settings...</span>
          </div>
        </div>
      ) : settings.length === 0 ? (
        <div className="p-8 text-center bg-white rounded-2xl shadow-xl">
          <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-gray-100 rounded-full">
            <Bell className="text-2xl text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            No Notification Settings Found
          </h3>
          <p className="text-gray-500">
            You don't have any notification settings configured yet.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {settings.map((notification) => (
            <div
              key={notification.notification_slug}
              className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-[1.02] transition-transform duration-300"
            >
              {/* Notification Header */}
              <div className="px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                    <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                      Notification Configuration
                    </h3>
                    <p className="text-xs text-gray-500 truncate">
                      ID: {notification.notification_slug.substring(0, 8)}...
                    </p>
                  </div>
                </div>
              </div>

              {/* Settings Form */}
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {/* Channels Section */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <h4 className="text-xs sm:text-sm font-medium text-gray-700">Channels</h4>
                      <div className="relative group flex-shrink-0">
                        <Info 
                          className="w-4 h-4 text-gray-400 cursor-help"
                          onMouseEnter={() => setActiveInfoTooltip("channel")}
                          onMouseLeave={() => setActiveInfoTooltip(null)}
                        />
                        {activeInfoTooltip === "channel" && (
                          <div className="absolute z-10 left-6 top-0 w-48 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
                            {infoTooltips.channel}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {channelOptions.map((option) => {
                        const Icon = option.icon;
                        const isSelected = notification.channel?.includes(option.value);
                        
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => handleChannelToggle(notification.notification_slug, option.value)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                              isSelected
                                ? "bg-blue-50 border-blue-300 text-blue-700"
                                : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            <Icon className={`w-4 h-4 ${isSelected ? "text-blue-600" : "text-gray-400"}`} />
                            <span className="text-sm font-medium">{option.label}</span>
                            {isSelected && <Check className="w-3 h-3 text-blue-600" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* SMS balance notification info (type is always low_balance) */}
                  <div className="space-y-2 p-3 rounded-lg bg-amber-50 border border-amber-100">
                    <div className="flex items-start gap-2">
                      <DollarSign className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs sm:text-sm text-amber-800">
                        This notification is for <strong>SMS balance alerts only</strong>. You will be notified when your balance falls below the threshold you set.
                      </p>
                    </div>
                  </div>

                  {/* Notification Time Section - inline toggles (8am, 1pm, 6pm) */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <h4 className="text-xs sm:text-sm font-medium text-gray-700">Notification Time</h4>
                      <div className="relative group flex-shrink-0">
                        <Info 
                          className="w-4 h-4 text-gray-400 cursor-help"
                          onMouseEnter={() => setActiveInfoTooltip("frequency")}
                          onMouseLeave={() => setActiveInfoTooltip(null)}
                        />
                        {activeInfoTooltip === "frequency" && (
                          <div className="absolute z-10 left-6 top-0 w-48 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
                            {infoTooltips.frequency}
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">Select one or more times to receive low balance alerts.</p>
                    <div className="flex flex-wrap gap-3">
                      {frequencyOptions.map((option) => {
                        const isSelected = notification.frequency?.includes(option.value);
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => handleFrequencyToggle(notification.notification_slug, option.value)}
                            className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border-2 transition-all duration-200 min-w-[7rem] justify-center ${
                              isSelected
                                ? "bg-indigo-50 border-indigo-400 text-indigo-700 shadow-sm ring-1 ring-indigo-200"
                                : "bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            <Clock className={`w-4 h-4 flex-shrink-0 ${isSelected ? "text-indigo-600" : "text-gray-400"}`} />
                            <span className="text-sm font-medium">{option.label}</span>
                            {isSelected && <Check className="w-4 h-4 text-indigo-600 flex-shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Threshold Section */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <h4 className="text-xs sm:text-sm font-medium text-gray-700 break-words">Threshold (SMS Units)</h4>
                      <div className="relative group flex-shrink-0">
                        <Info 
                          className="w-4 h-4 text-gray-400 cursor-help"
                          onMouseEnter={() => setActiveInfoTooltip("threshold")}
                          onMouseLeave={() => setActiveInfoTooltip(null)}
                        />
                        {activeInfoTooltip === "threshold" && (
                          <div className="absolute z-10 left-6 top-0 w-56 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
                            {infoTooltips.threshold}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        value={notification.threshold || 0}
                        onChange={(e) => handleThresholdChange(notification.notification_slug, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter threshold value"
                      />
                      <span className="text-sm text-gray-500 whitespace-nowrap">units</span>
                    </div>
                    <p className="text-xs text-gray-500">
                      You'll be notified when your balance falls below {notification.threshold || 0} units
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bottom Save Bar — in-flow for desktop/high-res (no scroll up) */}
      {hasChanges() && settings.length > 0 && (
        <div ref={bottomSaveBarRef} className="hidden md:block scroll-mt-4">
          <div className="relative overflow-hidden rounded-2xl border border-indigo-200/60 bg-gradient-to-br from-white via-indigo-50/30 to-white shadow-lg shadow-indigo-100/50">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-100/20 via-transparent to-transparent pointer-events-none" />
            <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-5 sm:p-6">
              <div className="flex items-start sm:items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
                  <Save className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Unsaved changes</p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {hasValidTimeSelection()
                      ? "Your notification preferences are ready to save."
                      : "Select at least one time (8:00 AM, 1:00 PM, or 6:00 PM) to enable Save."}
                  </p>
                </div>
              </div>
              <div className="flex gap-3 sm:shrink-0">
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reset
                </button>
                <button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={!canSave() || saving}
                  title={!hasValidTimeSelection() ? "Select at least one notification time to save" : undefined}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600"
                >
                  {saving ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save Bar for Mobile */}
      {hasChanges() && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg md:hidden z-10">
          {!hasValidTimeSelection() && (
            <p className="text-xs text-amber-700 mb-2 text-center">Select at least one time to save</p>
          )}
          <div className="flex gap-3 max-w-7xl mx-auto">
            <button
              onClick={handleReset}
              disabled={saving}
              className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg"
            >
              Reset
            </button>
            <button
              onClick={handleSaveSettings}
              disabled={!canSave() || saving}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white rounded-full animate-spin border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Profile = () => {
  const { txtconnect_ng_user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);
  const [showOtherState, setShowOtherState] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [logoutCountdown, setLogoutCountdown] = useState(0);
  const countdownIntervalRef = useRef(null);
  const [profileData, setProfileData] = useState({
    full_name: txtconnect_ng_user?.full_name || "",
    phone_number: txtconnect_ng_user?.phone_number || "",
    city: txtconnect_ng_user?.city || "",
    address: txtconnect_ng_user?.address || "",
    state: txtconnect_ng_user?.state || "",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  //Todo => Password validation
  const validatePassword = (password) => {
    const validations = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    return validations;
  };

  const passwordValidation = validatePassword(passwordData.newPassword);
  const passwordsMatch =
    passwordData.newPassword === passwordData.confirmPassword &&
    passwordData.confirmPassword !== "";

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const handlePasswordChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProfileChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Handle state selection - show/hide other input
    if (field === "state") {
      if (value === "Other") {
        setShowOtherState(true);
        setProfileData((prev) => ({
          ...prev,
          state: "",
        }));
      } else {
        setShowOtherState(false);
      }
    }
  };

  const handleEditToggle = async () => {
    if (isEditing) {
      // Save profile data
      await handleSaveProfile();
    } else {
      setIsEditing(true);
    }
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);

    try {
      // Prepare payload
      const payload = {
        full_name: profileData.full_name,
        address: profileData.address,
        state: profileData.state,
        city: profileData.city,
        phone_number: profileData.phone_number,
      };

      // Make API call
      const response = await profileRequests.updateProfile(payload);

      if (response.success && response.data?.data?.status_code === "000") {
        // Update Redux state with new user data
        const updatedUserData = {
          ...txtconnect_ng_user,
          ...response.data.data.data,
        };

        dispatch(
          setUser({
            userData: updatedUserData,
            token: localStorage.getItem("txtconnect_ng_user_token"),
          })
        );

        // Show success animation
        setUpdateSuccess(true);
        setTimeout(() => setUpdateSuccess(false), 3000);

        // Show success toast
        toast.success(" Profile updated successfully!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
            color: "white",
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(16, 185, 129, 0.3)",
          },
        });

        setIsEditing(false);
      } else {
        throw new Error(response.data?.data?.reason || "Update failed");
      }
    } catch (error) {
      console.error("Profile update error:", error);

      // Show error toast
      toast.error(
        `❌ ${error.message || "Failed to update profile. Please try again."}`,
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
            color: "white",
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(239, 68, 68, 0.3)",
          },
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setProfileData({
      full_name: txtconnect_ng_user?.full_name || "",
      phone_number: txtconnect_ng_user?.phone_number || "",
      city: txtconnect_ng_user?.city || "",
      address: txtconnect_ng_user?.address || "",
      state: txtconnect_ng_user?.state || "",
    });
    setIsEditing(false);
    setShowOtherState(false);
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        old_password: passwordData.currentPassword,
        password: passwordData.newPassword,
        password_confirmation: passwordData.confirmPassword,
      };

      const response = await profileRequests.changePassword(payload);

      if (response.success && response.data?.data?.status_code === "000") {
        // Show success toast with logout countdown
        toast.success(
          "🔐 Password changed successfully! Logging you out for security...",
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: true,
            style: {
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              color: "white",
              borderRadius: "12px",
              boxShadow: "0 10px 25px rgba(16, 185, 129, 0.3)",
            },
          }
        );

        // Reset password form
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });

        // Start logout countdown
        setLogoutCountdown(3);
        countdownIntervalRef.current = setInterval(() => {
          setLogoutCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current);

              // Clear user data and logout
              dispatch(clearUser());
              navigate("/login");
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        throw new Error(
          response.data?.data?.reason || "Password update failed"
        );
      }
    } catch (error) {
      console.error("Password update error:", error);

      // Show error toast
      toast.error(
        `❌ ${error.message || "Failed to update password. Please try again."}`,
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: {
            background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
            color: "white",
            borderRadius: "12px",
            boxShadow: "0 10px 25px rgba(239, 68, 68, 0.3)",
          },
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setShowPasswords({
      current: false,
      new: false,
      confirm: false,
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const tabs = [
    { id: "profile", name: "Profile Information", icon: "👤" },
    { id: "subscription", name: "Subscription", icon: "💳" },
    { id: "notifications", name: "Notifications", icon: "🔔" },
    { id: "security", name: "Security", icon: "🔒" },
  ];
//adding a commit

  return (
    <CustomerLayout>
      <div className="pt-20 min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
        <div className="px-4 py-8 mx-auto max-w-8xl sm:px-6 lg:px-8">
          {/* Success Animation Overlay */}
          {updateSuccess && (
            <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50 backdrop-blur-sm">
              <div className="p-8 bg-white rounded-3xl shadow-2xl animate-bounce transform">
                <div className="text-center">
                  <div className="flex justify-center items-center mx-auto mb-4 w-24 h-24 bg-gradient-to-r from-red-400 to-red-600 rounded-full animate-pulse">
                    <span className="text-4xl text-white">✅</span>
                  </div>
                  <h3 className="mb-2 text-2xl font-bold text-gray-900">
                    Profile Updated!
                  </h3>
                  <p className="text-gray-600">
                    Your changes have been saved successfully
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Logout Countdown Overlay */}
          {logoutCountdown > 0 && (
            <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-70 backdrop-blur-sm">
              <div className="p-8 mx-4 max-w-md bg-white rounded-3xl shadow-2xl animate-pulse transform">
                <div className="text-center">
                  <div className="flex justify-center items-center mx-auto mb-4 w-24 h-24 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full animate-bounce">
                    <span className="text-4xl text-white">🔒</span>
                  </div>
                  <h3 className="mb-2 text-2xl font-bold text-gray-900">
                    Password Changed Successfully!
                  </h3>
                  <p className="mb-4 text-gray-600">
                    For your security, you will be logged out automatically
                  </p>
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
                    <div className="flex justify-center items-center space-x-3">
                      <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full animate-spin">
                        <span className="text-2xl font-bold text-white">
                          {logoutCountdown}
                        </span>
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-semibold text-blue-800">
                          Logging out in
                        </p>
                        <p className="text-xs text-blue-600">Please wait...</p>
                      </div>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-gray-500">
                    You'll need to log in again with your new password
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center mb-4 space-x-4">
              <div className="flex justify-center items-center w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full shadow-lg">
                <span className="text-2xl font-bold text-white">
                  {txtconnect_ng_user?.full_name?.charAt(0) || "U"}
                </span>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {txtconnect_ng_user?.full_name || "User Profile"}
                </h1>
                <p className="text-gray-600">
                  Manage your account settings and preferences
                </p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-8 border-b border-gray-200">
            <nav className="flex -mb-px space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-all duration-200 ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600 transform scale-105"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <span
                    className={`mr-2 text-lg ${
                      activeTab === tab.id ? "animate-bounce" : ""
                    }`}
                  >
                    {tab.icon}
                  </span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {/* Profile Information Tab */}
            {activeTab === "profile" && (
              <div className="overflow-hidden bg-white rounded-3xl border border-gray-100 shadow-2xl">
                <div className="overflow-hidden relative px-8 py-6 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600">
                  <div className="absolute inset-0 bg-black opacity-10"></div>
                  <div className="flex relative items-center">
                    <div className="flex justify-center items-center w-12 h-12 bg-white bg-opacity-20 rounded-xl backdrop-blur-sm">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div className="ml-4">
                      <h2 className="text-2xl font-bold text-white">
                        Profile Information
                      </h2>
                      <p className="text-sm text-blue-100">
                        Manage your personal details and preferences
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-8 bg-gradient-to-br from-gray-50 to-white">
                  <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                    {/* Personal Information */}
                    <div className="space-y-6">
                      <div className="flex items-center mb-8">
                        <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-xl font-bold text-gray-900">
                            Personal Details
                          </h3>
                          <p className="text-sm text-gray-600">
                            Your basic information
                          </p>
                        </div>
                      </div>

                      <div className="group p-6 bg-white rounded-2xl border border-gray-200 transition-all duration-300 hover:shadow-xl hover:border-blue-200 hover:scale-[1.02]">
                        <div className="flex items-center mb-4">
                          <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100 transition-all duration-300 group-hover:from-blue-100 group-hover:to-indigo-100">
                            <User className="w-6 h-6 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-semibold tracking-wide text-gray-700 uppercase">
                              Full Name
                            </p>
                            {isEditing ? (
                              <input
                                type="text"
                                value={profileData.full_name}
                                onChange={(e) =>
                                  handleProfileChange(
                                    "full_name",
                                    e.target.value
                                  )
                                }
                                className="px-4 py-3 mt-2 w-full text-gray-900 bg-white rounded-xl border border-gray-300 shadow-sm transition-all duration-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none hover:border-blue-300"
                                placeholder="Enter your full name"
                              />
                            ) : (
                              <p className="mt-2 text-lg font-semibold text-gray-900">
                                {profileData.full_name || "Not provided"}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="group p-6 bg-white rounded-2xl border border-gray-200 transition-all duration-300 hover:shadow-xl hover:border-red-200 hover:scale-[1.02]">
                        <div className="flex items-center mb-4">
                          <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-red-50 to-red-50 rounded-xl border border-red-100 transition-all duration-300 group-hover:from-red-100 group-hover:to-red-100">
                            <Mail className="w-6 h-6 text-red-600" />
                          </div>
                          <div className="flex-1 ml-4">
                            <p className="text-sm font-semibold tracking-wide text-gray-700 uppercase">
                              Email Address
                            </p>
                            <input
                              type="email"
                              value={txtconnect_ng_user?.email || ""}
                              readOnly
                              className="px-4 py-3 mt-2 w-full text-gray-500 bg-gray-50 rounded-xl border border-gray-200 cursor-not-allowed"
                              placeholder="Email address"
                            />
                            {txtconnect_ng_user?.email_verified_at && (
                              <span className="inline-flex items-center px-3 py-1 mt-3 text-xs font-semibold text-red-700 bg-red-50 rounded-full border border-red-200 shadow-sm">
                                <CheckCircle className="mr-1 w-3 h-3" />
                                Verified
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="group p-6 bg-white rounded-2xl border border-gray-200 transition-all duration-300 hover:shadow-xl hover:border-purple-200 hover:scale-[1.02]">
                        <div className="flex items-center mb-4">
                          <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100 transition-all duration-300 group-hover:from-purple-100 group-hover:to-pink-100">
                            <Phone className="w-6 h-6 text-purple-600" />
                          </div>
                          <div className="flex-1 ml-4">
                            <p className="text-sm font-semibold tracking-wide text-gray-700 uppercase">
                              Phone Number
                            </p>
                            {isEditing ? (
                              <input
                                type="tel"
                                value={profileData.phone_number}
                                onChange={(e) =>
                                  handleProfileChange(
                                    "phone_number",
                                    e.target.value
                                  )
                                }
                                className="px-4 py-3 mt-2 w-full text-gray-900 bg-white rounded-xl border border-gray-300 shadow-sm transition-all duration-300 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none hover:border-purple-300"
                                placeholder="Enter your phone number"
                              />
                            ) : (
                              <p className="mt-2 text-lg font-semibold text-gray-900">
                                {profileData.phone_number || "Not provided"}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Location & Account Info */}
                    <div className="space-y-6">
                      <div className="flex items-center mb-8">
                        <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
                          <MapPin className="w-6 h-6 text-white" />
                        </div>
                        <div className="ml-4">
                          <h3 className="text-xl font-bold text-gray-900">
                            Location & Account
                          </h3>
                          <p className="text-sm text-gray-600">
                            Your address and account details
                          </p>
                        </div>
                      </div>

                      <div className="group p-6 bg-white rounded-2xl border border-gray-200 transition-all duration-300 hover:shadow-xl hover:border-orange-200 hover:scale-[1.02]">
                        <div className="flex items-center mb-4">
                          <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-100 transition-all duration-300 group-hover:from-orange-100 group-hover:to-amber-100">
                            <MapPin className="w-6 h-6 text-orange-600" />
                          </div>
                          <div className="flex-1 ml-4">
                            <p className="text-sm font-semibold tracking-wide text-gray-700 uppercase">
                              Address
                            </p>
                            {isEditing ? (
                              <input
                                type="text"
                                value={profileData.address}
                                onChange={(e) =>
                                  handleProfileChange("address", e.target.value)
                                }
                                className="px-4 py-3 mt-2 w-full text-gray-900 bg-white rounded-xl border border-gray-300 shadow-sm transition-all duration-300 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 focus:outline-none hover:border-orange-300"
                                placeholder="Enter your address"
                              />
                            ) : (
                              <p className="mt-2 text-lg font-semibold text-gray-900">
                                {profileData.address || "Not provided"}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="group p-6 bg-white rounded-2xl border border-gray-200 transition-all duration-300 hover:shadow-xl hover:border-yellow-200 hover:scale-[1.02]">
                        <div className="flex items-center mb-4">
                          <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl border border-yellow-100 transition-all duration-300 group-hover:from-yellow-100 group-hover:to-amber-100">
                            <Building className="w-6 h-6 text-yellow-600" />
                          </div>
                          <div className="flex-1 ml-4">
                            <p className="text-sm font-semibold tracking-wide text-gray-700 uppercase">
                              City
                            </p>
                            {isEditing ? (
                              <input
                                type="text"
                                value={profileData.city}
                                onChange={(e) =>
                                  handleProfileChange("city", e.target.value)
                                }
                                className="px-4 py-3 mt-2 w-full text-gray-900 bg-white rounded-xl border border-gray-300 shadow-sm transition-all duration-300 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 focus:outline-none hover:border-yellow-300"
                                placeholder="Enter your city"
                              />
                            ) : (
                              <p className="mt-2 text-lg font-semibold text-gray-900">
                                {profileData.city || "Not provided"}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="group p-6 bg-white rounded-2xl border border-gray-200 transition-all duration-300 hover:shadow-xl hover:border-red-200 hover:scale-[1.02]">
                        <div className="flex items-center mb-4">
                          <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-red-50 to-cyan-50 rounded-xl border border-red-100 transition-all duration-300 group-hover:from-red-100 group-hover:to-cyan-100">
                            <MapPin className="w-6 h-6 text-red-600" />
                          </div>
                          <div className="flex-1 ml-4">
                            <p className="text-sm font-semibold tracking-wide text-gray-700 uppercase">
                              Region
                            </p>
                            {isEditing ? (
                              <div className="mt-2 space-y-3">
                                {!showOtherState ? (
                                  <select
                                    value={profileData.state}
                                    onChange={(e) =>
                                      handleProfileChange(
                                        "state",
                                        e.target.value
                                      )
                                    }
                                    className="px-4 py-3 w-full text-gray-900 bg-white rounded-xl border border-gray-300 shadow-sm transition-all duration-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 focus:outline-none hover:border-red-300"
                                  >
                                    <option value="">Select your region</option>
                                    {States.map((state) => (
                                      <option
                                        key={state.value}
                                        value={state.value}
                                      >
                                        {state.name}
                                      </option>
                                    ))}
                                   
                                  </select>
                                ) : (
                                  <div className="space-y-2">
                                    <input
                                      type="text"
                                      value={profileData.state}
                                      onChange={(e) =>
                                        handleProfileChange(
                                          "state",
                                          e.target.value
                                        )
                                      }
                                      className="px-4 py-3 w-full text-gray-900 bg-white rounded-xl border border-gray-300 shadow-sm transition-all duration-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 focus:outline-none hover:border-red-300"
                                      placeholder="Enter your state/region"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setShowOtherState(false);
                                        setProfileData((prev) => ({
                                          ...prev,
                                          state: "",
                                        }));
                                      }}
                                      className="text-sm font-medium text-red-600 transition-colors duration-200 hover:text-red-800"
                                    >
                                      ← Back to dropdown
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <p className="mt-2 text-lg font-semibold text-gray-900">
                                {profileData.state || "Not provided"}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="group p-6 bg-white rounded-2xl border border-gray-200 transition-all duration-300 hover:shadow-xl hover:border-pink-200 hover:scale-[1.02]">
                        <div className="flex items-center mb-4">
                          <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-pink-50 to-rose-50 rounded-xl border border-pink-100 transition-all duration-300 group-hover:from-pink-100 group-hover:to-rose-100">
                            <Calendar className="w-6 h-6 text-pink-600" />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-semibold tracking-wide text-gray-700 uppercase">
                              Member Since
                            </p>
                            <p className="mt-2 text-lg font-semibold text-gray-900">
                              {txtconnect_ng_user?.created_at
                                ? formatDate(txtconnect_ng_user.created_at)
                                : "Unknown"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="group p-6 bg-white rounded-2xl border border-gray-200 transition-all duration-300 hover:shadow-xl hover:border-indigo-200 hover:scale-[1.02]">
                        <div className="flex items-center mb-4">
                          <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border border-indigo-100 transition-all duration-300 group-hover:from-indigo-100 group-hover:to-blue-100">
                            <Shield className="w-6 h-6 text-indigo-600" />
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-semibold tracking-wide text-gray-700 uppercase">
                              Account Type
                            </p>
                            <span className="inline-flex items-center px-4 py-2 mt-2 text-sm font-semibold text-indigo-700 capitalize bg-indigo-50 rounded-full border border-indigo-200 shadow-sm">
                              <Crown className="mr-2 w-4 h-4" />
                              {txtconnect_ng_user?.user_type || "Standard"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-8 mt-12 border-t border-gray-200">
                    {isEditing ? (
                      <div className="flex space-x-4">
                        <button
                          onClick={handleEditToggle}
                          disabled={isLoading}
                          className={`relative overflow-hidden flex items-center ${
                            isLoading
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-gradient-to-r from-red-600 to-red-600 hover:from-red-700 hover:to-red-700"
                          } text-white px-8 py-4 rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl font-semibold`}
                        >
                          {isLoading && (
                            <div className="flex absolute inset-0 justify-center items-center bg-gradient-to-r from-red-600 to-red-600">
                              <Loader2 className="w-5 h-5 text-white animate-spin" />
                            </div>
                          )}
                          <span
                            className={`flex items-center ${
                              isLoading ? "opacity-0" : "opacity-100"
                            }`}
                          >
                            <Save className="mr-3 w-5 h-5" />
                            {isLoading ? "Saving..." : "Save Changes"}
                          </span>
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={isLoading}
                          className="flex items-center px-8 py-4 font-semibold text-white bg-gradient-to-r from-gray-600 to-gray-700 rounded-2xl shadow-xl transition-all duration-300 transform hover:from-gray-700 hover:to-gray-800 hover:scale-105 hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <X className="mr-3 w-5 h-5" />
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={handleEditToggle}
                        className="flex items-center px-8 py-4 font-semibold text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl shadow-xl transition-all duration-300 transform hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 hover:scale-105 hover:shadow-2xl"
                      >
                        <Edit className="mr-3 w-5 h-5" />
                        Edit Profile
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Subscription Tab */}
            {activeTab === "subscription" && <SubscriptionTabContent />}

            {/* Notifications Tab */}
            {activeTab === "notifications" && <NotificationSettingsContent />}

            {/* Security Tab */}
            {activeTab === "security" && (
              <div className="space-y-6">
                {/* Security Overview */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-[1.02] transition-transform duration-300">
                  <div className="px-6 py-4 bg-gradient-to-r from-red-600 to-orange-600">
                    <h2 className="flex items-center text-xl font-semibold text-white">
                      <span className="mr-2">🔒</span>
                      Security Overview
                    </h2>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                      {/* Account Status */}
                      <div className="p-6 bg-gradient-to-br from-red-50 to-red-50 rounded-xl border-2 border-red-200 transition-all duration-200 hover:shadow-lg">
                        <div className="text-center">
                          <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full">
                            <span className="text-2xl">🛡️</span>
                          </div>
                          <h3 className="mb-2 font-semibold text-gray-900">
                            Account Status
                          </h3>
                          <span className="inline-flex items-center px-3 py-1 text-sm font-medium text-red-800 bg-red-100 rounded-full">
                            <span className="mr-1">✅</span>
                            Secure
                          </span>
                        </div>
                      </div>

                      {/* Email Verification */}
                      <div className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border-2 border-blue-200 transition-all duration-200 hover:shadow-lg">
                        <div className="text-center">
                          <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full">
                            <span className="text-2xl">📧</span>
                          </div>
                          <h3 className="mb-2 font-semibold text-gray-900">
                            Email Status
                          </h3>
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                              txtconnect_ng_user?.email_verified_at
                                ? "bg-red-100 text-red-800"
                                : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            <span className="mr-1">
                              {txtconnect_ng_user?.email_verified_at
                                ? "✅"
                                : "⚠️"}
                            </span>
                            {txtconnect_ng_user?.email_verified_at
                              ? "Verified"
                              : "Unverified"}
                          </span>
                        </div>
                      </div>

                      {/* Last Login */}
                      <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border-2 border-purple-200 transition-all duration-200 hover:shadow-lg">
                        <div className="text-center">
                          <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-purple-100 rounded-full">
                            <span className="text-2xl">⏰</span>
                          </div>
                          <h3 className="mb-2 font-semibold text-gray-900">
                            Last Login
                          </h3>
                          <p className="text-sm text-gray-600">
                            Today at 2:30 PM
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Password Management */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-[1.02] transition-transform duration-300">
                  <div className="px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600">
                    <h2 className="flex items-center text-xl font-semibold text-white">
                      <span className="mr-2">🔑</span>
                      Password Management
                    </h2>
                  </div>
                  <div className="p-6">
                    <div className="max-w-3xl">
                      <div className="p-4 mb-6 bg-blue-50 rounded-xl border border-blue-200">
                        <div className="flex items-start space-x-3">
                          <span className="text-2xl">💡</span>
                          <div>
                            <h4 className="mb-2 font-semibold text-blue-900">
                              Password Security Tips
                            </h4>
                            <ul className="space-y-1 text-sm text-blue-800">
                              <li>
                                • Use a unique password for your TxtConnect
                                account
                              </li>
                              <li>
                                • Include a mix of letters, numbers, and special
                                characters
                              </li>
                              <li>
                                • Avoid using personal information in your
                                password
                              </li>
                              <li>• Consider using a password manager</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <form
                        className="space-y-6"
                        onSubmit={handlePasswordUpdate}
                      >
                        {/* Current Password */}
                        <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                          <label className="flex items-center mb-3 text-sm font-medium text-gray-700">
                            <span className="mr-2">🔐</span>
                            Current Password
                          </label>
                          <div className="relative">
                            <input
                              type={showPasswords.current ? "text" : "password"}
                              value={passwordData.currentPassword}
                              onChange={(e) =>
                                handlePasswordChange(
                                  "currentPassword",
                                  e.target.value
                                )
                              }
                              className="px-4 py-3 pr-12 w-full bg-white rounded-lg border border-gray-300 shadow-sm transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              placeholder="Enter your current password"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                togglePasswordVisibility("current")
                              }
                              className="flex absolute inset-y-0 right-0 items-center pr-3 rounded-r-lg transition-colors duration-200 hover:bg-gray-100"
                            >
                              <span className="text-lg">
                                {showPasswords.current ? "🙈" : "👁️"}
                              </span>
                            </button>
                          </div>
                        </div>

                        {/* New Password */}
                        <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                          <label className="flex items-center mb-3 text-sm font-medium text-gray-700">
                            <span className="mr-2">🆕</span>
                            New Password
                          </label>
                          <div className="relative">
                            <input
                              type={showPasswords.new ? "text" : "password"}
                              value={passwordData.newPassword}
                              onChange={(e) =>
                                handlePasswordChange(
                                  "newPassword",
                                  e.target.value
                                )
                              }
                              className="px-4 py-3 pr-12 w-full bg-white rounded-lg border border-gray-300 shadow-sm transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              placeholder="Enter your new password"
                            />
                            <button
                              type="button"
                              onClick={() => togglePasswordVisibility("new")}
                              className="flex absolute inset-y-0 right-0 items-center pr-3 rounded-r-lg transition-colors duration-200 hover:bg-gray-100"
                            >
                              <span className="text-lg">
                                {showPasswords.new ? "🙈" : "👁️"}
                              </span>
                            </button>
                          </div>

                          {/* Enhanced Password Validation */}
                          {passwordData.newPassword && (
                            <div className="p-4 mt-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                              <div className="flex justify-between items-center mb-3">
                                <h4 className="flex items-center text-sm font-medium text-gray-700">
                                  <span className="mr-2">📋</span>
                                  Password Strength
                                </h4>
                                <div
                                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                                    Object.values(passwordValidation).filter(
                                      Boolean
                                    ).length >= 4
                                      ? "bg-red-100 text-red-800"
                                      : Object.values(
                                          passwordValidation
                                        ).filter(Boolean).length >= 2
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-red-100 text-red-800"
                                  }`}
                                >
                                  {Object.values(passwordValidation).filter(
                                    Boolean
                                  ).length >= 4
                                    ? "Strong"
                                    : Object.values(passwordValidation).filter(
                                        Boolean
                                      ).length >= 2
                                    ? "Medium"
                                    : "Weak"}
                                </div>
                              </div>

                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <div className="flex items-center p-2 space-x-3 rounded-lg transition-colors duration-200">
                                  <div
                                    className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                      passwordValidation.length
                                        ? "bg-red-100"
                                        : "bg-red-100"
                                    }`}
                                  >
                                    <span className="text-sm">
                                      {passwordValidation.length ? "✅" : "❌"}
                                    </span>
                                  </div>
                                  <span
                                    className={`text-sm font-medium ${
                                      passwordValidation.length
                                        ? "text-red-700"
                                        : "text-red-700"
                                    }`}
                                  >
                                    8+ characters
                                  </span>
                                </div>

                                <div className="flex items-center p-2 space-x-3 rounded-lg transition-colors duration-200">
                                  <div
                                    className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                      passwordValidation.uppercase
                                        ? "bg-red-100"
                                        : "bg-red-100"
                                    }`}
                                  >
                                    <span className="text-sm">
                                      {passwordValidation.uppercase
                                        ? "✅"
                                        : "❌"}
                                    </span>
                                  </div>
                                  <span
                                    className={`text-sm font-medium ${
                                      passwordValidation.uppercase
                                        ? "text-red-700"
                                        : "text-red-700"
                                    }`}
                                  >
                                    Uppercase letter
                                  </span>
                                </div>

                                <div className="flex items-center p-2 space-x-3 rounded-lg transition-colors duration-200">
                                  <div
                                    className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                      passwordValidation.lowercase
                                        ? "bg-red-100"
                                        : "bg-red-100"
                                    }`}
                                  >
                                    <span className="text-sm">
                                      {passwordValidation.lowercase
                                        ? "✅"
                                        : "❌"}
                                    </span>
                                  </div>
                                  <span
                                    className={`text-sm font-medium ${
                                      passwordValidation.lowercase
                                        ? "text-red-700"
                                        : "text-red-700"
                                    }`}
                                  >
                                    Lowercase letter
                                  </span>
                                </div>

                                <div className="flex items-center p-2 space-x-3 rounded-lg transition-colors duration-200">
                                  <div
                                    className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                      passwordValidation.number
                                        ? "bg-red-100"
                                        : "bg-red-100"
                                    }`}
                                  >
                                    <span className="text-sm">
                                      {passwordValidation.number ? "✅" : "❌"}
                                    </span>
                                  </div>
                                  <span
                                    className={`text-sm font-medium ${
                                      passwordValidation.number
                                        ? "text-red-700"
                                        : "text-red-700"
                                    }`}
                                  >
                                    Number
                                  </span>
                                </div>

                                <div className="flex items-center p-2 space-x-3 rounded-lg transition-colors duration-200">
                                  <div
                                    className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                      passwordValidation.special
                                        ? "bg-red-100"
                                        : "bg-red-100"
                                    }`}
                                  >
                                    <span className="text-sm">
                                      {passwordValidation.special ? "✅" : "❌"}
                                    </span>
                                  </div>
                                  <span
                                    className={`text-sm font-medium ${
                                      passwordValidation.special
                                        ? "text-red-700"
                                        : "text-red-700"
                                    }`}
                                  >
                                    Special character
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Confirm Password */}
                        <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                          <label className="flex items-center mb-3 text-sm font-medium text-gray-700">
                            <span className="mr-2">🔄</span>
                            Confirm New Password
                          </label>
                          <div className="relative">
                            <input
                              type={showPasswords.confirm ? "text" : "password"}
                              value={passwordData.confirmPassword}
                              onChange={(e) =>
                                handlePasswordChange(
                                  "confirmPassword",
                                  e.target.value
                                )
                              }
                              className="px-4 py-3 pr-12 w-full bg-white rounded-lg border border-gray-300 shadow-sm transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                              placeholder="Confirm your new password"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                togglePasswordVisibility("confirm")
                              }
                              className="flex absolute inset-y-0 right-0 items-center pr-3 rounded-r-lg transition-colors duration-200 hover:bg-gray-100"
                            >
                              <span className="text-lg">
                                {showPasswords.confirm ? "🙈" : "👁️"}
                              </span>
                            </button>
                          </div>

                          {/* Password Match Validation */}
                          {passwordData.confirmPassword && (
                            <div className="p-3 mt-4 rounded-lg border transition-all duration-200">
                              <div className="flex items-center space-x-3">
                                <div
                                  className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                    passwordsMatch ? "bg-red-100" : "bg-red-100"
                                  }`}
                                >
                                  <span className="text-sm">
                                    {passwordsMatch ? "✅" : "❌"}
                                  </span>
                                </div>
                                <span
                                  className={`text-sm font-medium ${
                                    passwordsMatch
                                      ? "text-red-700"
                                      : "text-red-700"
                                  }`}
                                >
                                  {passwordsMatch
                                    ? "Passwords match perfectly!"
                                    : "Passwords do not match"}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Submit Button */}
                        <div className="flex pt-6 space-x-4">
                          <button
                            type="submit"
                            disabled={
                              !Object.values(passwordValidation).every(
                                Boolean
                              ) ||
                              !passwordsMatch ||
                              !passwordData.currentPassword ||
                              isLoading
                            }
                            className={`relative overflow-hidden ${
                              isLoading ||
                              !Object.values(passwordValidation).every(
                                Boolean
                              ) ||
                              !passwordsMatch ||
                              !passwordData.currentPassword
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
                            } text-white px-8 py-3 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl disabled:shadow-none font-medium`}
                          >
                            {isLoading && (
                              <div className="flex absolute inset-0 justify-center items-center bg-gradient-to-r from-indigo-600 to-purple-600">
                                <div className="w-5 h-5 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
                              </div>
                            )}
                            <span
                              className={`flex items-center ${
                                isLoading ? "opacity-0" : "opacity-100"
                              }`}
                            >
                              <span className="mr-2">🔄</span>
                              {isLoading ? "Updating..." : "Update Password"}
                            </span>
                          </button>

                          <button
                            type="button"
                            onClick={handlePasswordCancel}
                            disabled={isLoading}
                            className="px-8 py-3 font-medium text-gray-700 bg-gray-100 rounded-xl shadow-lg transition-all duration-200 transform hover:bg-gray-200 hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <span className="mr-2">❌</span>
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default Profile;